import { ALL_LANGUAGES, Language } from '@mi-front/core';
import { Autocomplete, AutocompleteProps, TextField } from '@mui/material';
import { useIntl } from 'react-intl';
import { CountryFlag } from './CountryFlag';

/**
 * Extracts ISO 3166-1 alpha-2 country code from a locale id (e.g. "en-US" -> "us", "es-AR" -> "ar").
 */
function getCountryCodeFromLocale(localeId: string): string {
  const part = localeId.split('-')[1];
  return (part ?? localeId.slice(0, 2)).toLowerCase();
}

export type LanguageSelectProps = {
  readonly value?: string | null;
  readonly onChange: (languageId: string | null) => void;
  readonly onBlur?: (e: React.FocusEvent<any, Element>) => void;
  readonly allowed?: readonly string[];
  readonly disabled?: boolean;
  readonly error?: string | boolean;
  readonly autocompleteProps?: Omit<
    AutocompleteProps<Language, false, boolean, false>,
    | 'onChange'
    | 'value'
    | 'options'
    | 'getOptionLabel'
    | 'isOptionEqualToValue'
    | 'renderInput'
    | 'renderOption'
    | 'onBlur'
    | 'disabled'
    | 'disableClearable'
  >;
  readonly required?: boolean;
  readonly allowEmpty?: boolean;
};

/**
 * Language selector that shows country labels and flag icons from Flagpedia CDN.
 * Based on the libs/ui LanguageSelectAlt, with flags from https://flagcdn.com.
 */
export function LanguageSelect(props: LanguageSelectProps) {
  const {
    value,
    onChange,
    onBlur,
    allowed,
    disabled,
    error,
    autocompleteProps,
    required,
    allowEmpty = false,
  } = props;
  const { formatMessage } = useIntl();

  function isAllowed(l: Language) {
    return !allowed || !allowed.length || allowed.includes(l.id);
  }

  const filteredLanguages = [...ALL_LANGUAGES.filter(isAllowed)].sort((a, b) =>
    a.name.localeCompare(b.name),
  );

  const selectedLanguage = value ? filteredLanguages.find((l) => l.id === value) || null : null;

  const handleChange = (_event: React.SyntheticEvent, newValue: Language | null) => {
    if (newValue) {
      onChange(newValue.id);
    } else {
      onChange(null);
    }
  };

  return (
    <Autocomplete<Language, false, boolean, false>
      options={filteredLanguages}
      value={selectedLanguage}
      onChange={handleChange}
      onBlur={onBlur}
      disabled={disabled}
      autoHighlight
      disableClearable={!allowEmpty}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, val) => option.id === val.id}
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          <CountryFlag countryCode={getCountryCodeFromLocale(option.id)} sx={{ mr: 1.5 }} />
          {option.name}
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={formatMessage({
            id: 'common.languageSelect.defaultLabel',
            defaultMessage: 'Idioma',
          })}
          error={!!error}
          helperText={typeof error === 'string' ? error : undefined}
          fullWidth
          required={required}
          InputProps={{
            ...params.InputProps,
            startAdornment: selectedLanguage ? (
              <>
                <CountryFlag
                  countryCode={getCountryCodeFromLocale(selectedLanguage.id)}
                  sx={{ ml: 1.5, mr: 0.5 }}
                />
                {params.InputProps.startAdornment}
              </>
            ) : (
              params.InputProps.startAdornment
            ),
          }}
        />
      )}
      {...autocompleteProps}
    />
  );
}
